'use strict';

/**
 * Sentry noise filtering for the Atlas POS app (ATL-563).
 *
 * A POS runs all day on flaky mobile data, so transient connectivity failures,
 * benign browser-API denials, and expected form-validation messages are normal
 * operating conditions — not crashes. Reporting them drowns the real signal and
 * burns quota (~81% of a 24h sweep). We drop those classes before they reach
 * Sentry while keeping every genuine error (real HTTP 4xx/5xx, Zod/api-client
 * rejections, cyclical-ref, RangeError, authenticated permission bugs, etc.).
 *
 * Two layers:
 *   1. `SENTRY_IGNORE_ERRORS` — stable string classes for Sentry's `ignoreErrors`.
 *   2. `createSentryBeforeSend()` — a `beforeSend` hook for the structured cases
 *      a string match can't safely catch (offline axios errors vs real HTTP
 *      faults; pre-auth permission-denied vs an authenticated rules bug).
 *
 * ⚠️ THE TWO LAYERS ARE NOT INTERCHANGEABLE, AND ONLY ONE OF THEM CAN SEE AN EVENT'S
 * TAGS (ATL-985). `ignoreErrors` is enforced by the InboundFilters *event processor*,
 * which the SDK runs inside `_prepareEvent` — strictly BEFORE `beforeSend`, and with
 * nothing to inspect but the event's message strings. So any class that must be kept
 * ON A MARKER rather than on its message has to be filtered in layer 2; putting it in
 * layer 1 makes the marker unreachable. That is exactly how the dual-write bleed stayed
 * invisible: `/Network Error/` sat in layer 1, so a `supabase_dual_write_error` event
 * from an offline till was discarded before any code could notice what it was.
 *
 * Authored as CommonJS (like sibling `sentryRelease.js`) so it is trivially
 * unit-testable under Jest while Metro still bundles it for the app.
 */

/**
 * Stable message classes safe to drop wholesale via Sentry `ignoreErrors`.
 * Matched against the event's exception message/type by the SDK.
 *
 * NOTE (ATL-985): the two network-error patterns deliberately DO NOT live here — see
 * `NETWORK_NOISE_PATTERNS` below and the docblock's warning. Everything remaining is a
 * class no marker can ever legitimately override.
 */
const SENTRY_IGNORE_ERRORS = [
  // Browser denied the Wake Lock request (NotAllowedError) — no user impact.
  /Wake Lock/,
  // User-facing form-validation messages thrown as Errors, both locales:
  // ru «Пожалуйста заполните поле …» / en "Please fill in the … field".
  /Пожалуйста заполните поле/,
  /Please fill in the .* field/,
];

/**
 * Offline-connectivity message classes. These used to be `ignoreErrors` entries; they were
 * moved into `beforeSend` by ATL-985 so that a dual-write failure carrying this message is
 * still reachable when the decision to drop it is made. Ordinary events matching them are
 * dropped exactly as before — one layer later, with no behavioural difference.
 */
const NETWORK_NOISE_PATTERNS = [/Network Error/, /A network error occurred/];

/**
 * Tag set by `bestEffortDualWrite`'s failure reporter on every Supabase dual-write failure.
 * An event carrying it is not connectivity noise: Firestore has ALREADY committed, so the
 * Supabase row is missing and stays missing. It is a data-loss report that happens to have a
 * network error attached, and it must never be filtered on the error's shape.
 */
const DUAL_WRITE_MARKER_TAG = 'supabase_dual_write_error';

/**
 * Aggregation window for dual-write TRANSPORT failures (ATL-985).
 *
 * These events were previously dropped wholesale, so un-dropping them adds volume that was
 * never budgeted — and an offline till retries a failing sale continuously, so "keep them all"
 * would trade one broken outcome (blind) for another (quota exhausted, which is also blind).
 *
 * What the investigation needs from this class is PRESENCE and RATE per signature, not one
 * event per occurrence: "is org X losing writes, from which module, and were they queued".
 * So the first event of a signature is sent immediately (detection is never delayed) and the
 * rest of the window is counted instead, with the count riding on the next event that goes
 * out. The rate survives; the volume does not. 30 minutes puts a worst-case till at 2 events
 * per signature per hour while keeping the aggregate exact.
 */
const DUAL_WRITE_NETWORK_SAMPLE_WINDOW_MS = 30 * 60 * 1000;

/**
 * Hard cap on tracked signatures. Cardinality is bounded by construction (one org per device
 * × ~20 modules × ~5 ops × 2 queued states), so this is a safety net against an unforeseen
 * high-cardinality tag, not an expected limit. Eviction is oldest-first — the signature that
 * just arrived is the one worth keeping.
 */
const DUAL_WRITE_SAMPLE_MAX_KEYS = 500;

/** signature → { openedAt, suppressed }. Module-level: the window is per device session. */
const dualWriteNetworkWindows = new Map();

/** Pull the underlying thrown value out of a Sentry event hint. */
function getOriginalException(hint) {
  if (!hint) return undefined;
  return hint.originalException !== undefined ? hint.originalException : hint.syntheticException;
}

/**
 * Offline axios/fetch failure: the request never reached the server, so there is
 * no HTTP response. Real server faults (4xx/5xx carry `error.response`) are kept.
 *
 * Timeouts are deliberately NOT treated as offline noise: `ECONNABORTED` /
 * `ETIMEDOUT` mean the backend was reachable but slow/hanging (e.g. a cold start
 * or DB stall) — that is an availability signal worth keeping in Sentry. True
 * offline spikes are caught separately by rate-alerting (ATL-464).
 */
function isNetworkError(error) {
  if (!error || typeof error !== 'object') return false;
  // Keep timeouts — reachable-but-slow backend, not offline. Checked before the
  // generic no-response branch below, which would otherwise swallow them.
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return false;
  if (error.code === 'ERR_NETWORK') return true;
  if (error.message === 'Network Error') return true;
  // axios shape: a request was made but no response was received (offline).
  if (error.request && !error.response) return true;
  return false;
}

/**
 * The message strings the SDK's own `ignoreErrors` would have matched, in the same order.
 * Mirrors `_getPossibleEventMessages` in `@sentry/core`'s InboundFilters so that moving the
 * network patterns into `beforeSend` (ATL-985) drops exactly the same events it used to —
 * including message-only captures, which have no `originalException` to inspect.
 */
function eventMessages(event) {
  const messages = [];
  if (!event || typeof event !== 'object') return messages;
  if (typeof event.message === 'string') messages.push(event.message);
  const values = event.exception && event.exception.values;
  const last = Array.isArray(values) && values.length ? values[values.length - 1] : undefined;
  if (last && typeof last.value === 'string') {
    messages.push(last.value);
    if (typeof last.type === 'string') messages.push(`${last.type}: ${last.value}`);
  }
  return messages;
}

/**
 * Offline connectivity, judged on the structured error AND on the message strings the
 * `ignoreErrors` layer used to judge on. Both are needed: the structured check keeps timeouts
 * (reachable-but-slow backend) that a naive string match would swallow, and the string check
 * keeps the classes that arrive without a usable exception object.
 */
function isNetworkNoiseEvent(event, error) {
  if (isNetworkError(error)) return true;
  // The message branch is deliberately unguarded: layer 1 matched on messages ALONE and ran
  // first, so an event whose message matches was already being dropped regardless of the shape
  // of its exception. Adding a shape guard here would not preserve old behaviour, it would
  // change it — and it would silently un-drop classes like `A network error occurred.`, whose
  // Error object carries no axios fields at all.
  return eventMessages(event).some((message) =>
    NETWORK_NOISE_PATTERNS.some((pattern) => pattern.test(message)),
  );
}

/**
 * Sentry serialises boolean tag values capitalised on the wire (`True`/`False`), and an event
 * may reach `beforeSend` either before or after that hop. Read the marker truthily rather than
 * with `=== true`, or the fix would work in tests and fail in production.
 */
function hasTruthyTag(value) {
  if (value === true) return true;
  if (typeof value !== 'string') return false;
  const normalized = value.toLowerCase();
  return normalized === 'true' || normalized === '1';
}

/** Is this event a Supabase dual-write failure report? */
function isDualWriteEvent(event) {
  return Boolean(event && event.tags && hasTruthyTag(event.tags[DUAL_WRITE_MARKER_TAG]));
}

/** Low-cardinality signature: the four dimensions a bleed investigation actually asks about. */
function dualWriteSampleKey(tags) {
  return [
    tags.dual_write_org === undefined ? '?' : String(tags.dual_write_org),
    tags.dual_write_module === undefined ? '?' : String(tags.dual_write_module),
    tags.dual_write_op === undefined ? '?' : String(tags.dual_write_op),
    tags.dual_write_outbox_queued === undefined ? '?' : String(tags.dual_write_outbox_queued),
  ].join('|');
}

/**
 * The window bookkeeping both dual-write samplers share: `null` when this event falls inside the
 * open window for `key` (it is counted instead of sent), otherwise how many identical events the
 * previous window suppressed — the count the event that goes out carries.
 */
function openDualWriteSampleWindow(key) {
  const now = Date.now();
  const open = dualWriteNetworkWindows.get(key);

  if (open && now - open.openedAt < DUAL_WRITE_NETWORK_SAMPLE_WINDOW_MS) {
    open.suppressed += 1;
    return null;
  }

  const suppressedSincePrevious = open ? open.suppressed : 0;
  // Re-insert (delete first) so Map iteration order stays least-recently-opened first and the
  // eviction below can never discard the signature that just arrived.
  dualWriteNetworkWindows.delete(key);
  dualWriteNetworkWindows.set(key, { openedAt: now, suppressed: 0 });
  while (dualWriteNetworkWindows.size > DUAL_WRITE_SAMPLE_MAX_KEYS) {
    const oldest = dualWriteNetworkWindows.keys().next();
    if (oldest.done) break;
    dualWriteNetworkWindows.delete(oldest.value);
  }
  return suppressedSincePrevious;
}

/**
 * Is this dual-write failure a refusal by a migration pause (ATL-1805)? `dualWrite.ts` tags it
 * `dual_write_migration_paused` only when true.
 */
function isDualWriteMigrationPausedEvent(event) {
  return isDualWriteEvent(event) && hasTruthyTag(event.tags.dual_write_migration_paused);
}

/**
 * ATL-1805 — a write refused because its organization is being re-migrated. Before ATL-1805 the
 * pause answered a synthetic 200 and these produced no event at all; now every live write of a
 * paused organization fails with a 503 the outbox queues and holds, for the whole run — hours on
 * a large organization, several dual-writes per sale. Each is the pause working, not a loss (the
 * `dual_write_outbox_queued` dimension of the key keeps a write the outbox could NOT queue in its
 * own signature, so that loss is still reported on its first occurrence). Same window as transport
 * failures: presence and rate per signature, not one event per write. The fingerprint is the
 * class-wide one `dualWrite.ts` set; it is left alone.
 */
function sampleDualWriteMigrationPausedEvent(event) {
  const tags = event.tags || {};
  const key = `migration-paused|${dualWriteSampleKey(tags)}`;
  const suppressedSincePrevious = openDualWriteSampleWindow(key);
  if (suppressedSincePrevious === null) return null;
  event.contexts = {
    ...(event.contexts || {}),
    dual_write_sampling: { key, windowMs: DUAL_WRITE_NETWORK_SAMPLE_WINDOW_MS, suppressedSincePrevious },
  };
  return event;
}

/**
 * Keep the first dual-write transport failure of each signature per window; count the rest.
 * Returns the event to send, or null when it was folded into the running count.
 */
function sampleDualWriteTransportEvent(event) {
  const tags = event.tags || {};
  const key = dualWriteSampleKey(tags);
  const suppressedSincePrevious = openDualWriteSampleWindow(key);
  if (suppressedSincePrevious === null) return null;

  event.tags = {
    ...tags,
    // The class that was structurally invisible until ATL-985 — query it directly.
    // Sentry renders boolean tag values capitalised: search `dual_write_transport_failure:True`.
    dual_write_transport_failure: true,
  };
  event.contexts = {
    ...(event.contexts || {}),
    dual_write_sampling: {
      key,
      windowMs: DUAL_WRITE_NETWORK_SAMPLE_WINDOW_MS,
      // How many identical failures this one event stands for. Non-zero means the device is
      // failing repeatedly — the rate is here even though the volume is not.
      suppressedSincePrevious,
    },
  };
  // Its own issue, not the existing `supabase-dual-write/<module>/<op>` bucket. These events
  // were never sent before, so a separate fingerprint disturbs nothing — whereas folding a
  // newly-unblocked class into an issue already holding thousands of TIMEOUT events would make
  // "is this getting worse" unanswerable for both causes at once. Same reasoning the failure
  // path already applies when it splits defects out by field path.
  event.fingerprint = [
    'supabase-dual-write-transport',
    String(tags.dual_write_module === undefined ? '?' : tags.dual_write_module),
    String(tags.dual_write_op === undefined ? '?' : tags.dual_write_op),
  ];
  return event;
}

/** TEST-ONLY: clear the sampling windows so cases cannot leak state into each other. */
function resetSentryFiltersForTests() {
  dualWriteNetworkWindows.clear();
}

/**
 * Expected user-input validation surfaced as a thrown Error. Throw sites can join
 * several messages (`errors.join('\n')`) or prefix the product name, so we match a
 * substring/pattern rather than the whole message. Covers ru and en locales.
 */
function isFormValidationError(error) {
  const message = error && error.message;
  if (typeof message !== 'string') return false;
  return message.includes('Пожалуйста заполните поле') || /Please fill in the .* field/.test(message);
}

/** Firestore "permission-denied" / "Missing or insufficient permissions". */
function isPermissionDeniedError(error) {
  if (!error || typeof error !== 'object') return false;
  if (error.code === 'permission-denied') return true;
  const message = typeof error.message === 'string' ? error.message : '';
  return /Missing or insufficient permissions/.test(message);
}

// ---------------------------------------------------------------------------------------------
// ATL-2035 — Sentry carries write failures and crashes, and nothing else.
// ---------------------------------------------------------------------------------------------

/**
 * WHY THIS IS PERMANENT, NOT A SWITCH.
 *
 * The Sentry org `qarasay` is on the free Developer plan: 5,000 errors per billing month (cycle
 * starts on the 28th), on-demand impossible (`GET /api/0/customers/qarasay/`). It stays there
 * (owner decision 2026-11-02). Demand was ~23–38k errors/month, so the quota ran out on day 4–12
 * of every cycle since 2026-08-28 and every later event was refused (stats_v2 `rate_limited`,
 * reason `error_usage_exceeded`) — including the dual-write and books losses that arrived in the
 * dark days. The events that ate the quota were read-path noise: of 4,755 accepted 2026-10-28..31,
 * 1,993 were `api_error` GET failures and 708 were a duplicate report of one daily-stats timeout.
 *
 * THE RULE: an event reaches Sentry only if it is
 *   - a WRITE FAILURE — dual-write, outbox, write-claim, stock mirror, books writer, fiscal sign,
 *     daily-stats repair (`isReportableEvent`; tag-keyed, so a new reporter must carry a marker —
 *     the source-contract test enforces it for the loss modules), or a raw-apiClient failure whose
 *     method is not a read; or
 *   - a CRASH — unhandled or fatal.
 * Everything else is dropped. No sampling anywhere: a write failure or crash is never thrown away
 * on chance.
 *
 * REPEATS: within `SENTRY_REPEAT_WINDOW_MS`, the second and later events of the same signature on
 * the same device are held back from Sentry — a new class, or the same class on another device,
 * always gets through (the window is per signature and lives on the device). The number held back
 * rides on the next event of that signature (`contexts.sentry_repeats.suppressedSincePrevious`),
 * and EVERY occurrence — sent, held back as a repeat, or filtered upstream — is counted through
 * `onReportableEvent` into PostHog (`sentry_reportable_event`, grouped by organization), so the
 * full rate stays countable when Sentry only holds the first of each hour.
 *
 * Measured against the protected events Sentry accepted 2026-10-28..31 (616 in 4 days, ≈4.6k/30d):
 * with the web Wake Lock rejections fixed at their source (App.js) and the hourly repeat window,
 * ≈2.6k/30d. 2026-10-03..04: ≈1.2k/30d.
 */
const SENTRY_REPEAT_WINDOW_MS = 60 * 60 * 1000;

/** Bound on tracked signatures (oldest-first eviction), as for the dual-write windows. */
const SENTRY_REPEAT_MAX_KEYS = 500;

/** PostHog event every reportable occurrence is counted under. */
const SENTRY_REPORTABLE_POSTHOG_EVENT = 'sentry_reportable_event';

/**
 * Tag prefixes that mark a write failure: dual-write, outbox, write-claim, stock mirror, books,
 * fiscal, daily-stats repair. A tag KEY with one of these prefixes qualifies the event whatever
 * its value — a `dual_write_outbox_queued:False` event is a loss report.
 */
const WRITE_FAILURE_TAG_PREFIXES = [
  'dual_write',
  'supabase_dual_write',
  'outbox_',
  'write_claim',
  'stock_mirror',
  'books_',
  'fiscal',
  'daily_stats_repair',
  'daily_stats_drift',
  'daily_stats_sweep',
  'daily_stats_amount',
];

/**
 * Books writers whose capture sites use a one-off tag rather than a prefix above. Each is a
 * write to money or stock (or to the daily stats derived from them) that failed or was refused.
 */
const WRITE_FAILURE_TAG_KEYS = [
  'write_back_fallback',
  'cart_item_non_finite',
  'delete_sale_dict_miss',
  'sale_dual_write_outcome_callback_threw',
  'cumulative_refund_refusal',
  'sale_return',
  'save_new_store_backfill_failed',
  'delete_store_prune_failed',
  'atl1131_working_hours_missing_employee_id',
];

/** HTTP methods that read. A raw-apiClient failure with any other method may be a books write. */
const READ_METHODS = new Set(['get', 'head', 'options']);

/** signature → { openedAt, suppressed }. Module-level: per device session. */
const repeatWindows = new Map();

/** A crash: an exception the app did not handle, or a fatal-level event. */
function isCrashEvent(event) {
  if (event.level === 'fatal') return true;
  const values = event.exception && event.exception.values;
  if (!Array.isArray(values)) return false;
  return values.some((v) => v && v.mechanism && v.mechanism.handled === false);
}

/** The write-failure marker this event carries, or null. */
function writeFailureMarker(event) {
  const tags = event.tags || {};
  for (const key of Object.keys(tags)) {
    if (WRITE_FAILURE_TAG_KEYS.includes(key)) return key;
    if (WRITE_FAILURE_TAG_PREFIXES.some((prefix) => key.startsWith(prefix))) return key;
  }
  // The raw apiClient interceptor reports every failed request. A failed READ is noise; a failed
  // write through it may be a books write whose caller reports nothing, so keep it. An unknown
  // method is kept: wrongly keeping costs one event, wrongly dropping costs a loss report.
  if (hasTruthyTag(tags.api_error)) {
    const method = event.extra && typeof event.extra.method === 'string' ? event.extra.method : '';
    if (!READ_METHODS.has(method.toLowerCase())) return 'api_error';
  }
  // The Firestore SDK refusing a write it was handed (`WriteBatch.set() called with invalid data`,
  // an `undefined` field): the books write did NOT happen, whoever called it and however it was
  // tagged — QARASAY-KASSA-314/31B arrived untagged. Matched on the SDK's own method name, so a
  // read never qualifies.
  if (eventMessages(event).some(isFirestoreInvalidWriteMessage)) return 'firestore_invalid_write';
  return null;
}

const FIRESTORE_WRITE_METHOD_PATTERN = /\b(WriteBatch\.(set|update|delete)|Transaction\.(set|update|delete)|(DocumentReference\.)?(setDoc|updateDoc|addDoc)|DocumentReference\.(set|update))\(\)/;

function isFirestoreInvalidWriteMessage(message) {
  return typeof message === 'string' && /invalid data/i.test(message) && FIRESTORE_WRITE_METHOD_PATTERN.test(message);
}

/**
 * Which reportable class this event belongs to — `'write'`, `'crash'` — or null when Sentry
 * should not carry it. A malformed event is kept as a crash: never drop on uncertainty.
 */
function reportableKind(event) {
  if (!event || typeof event !== 'object') return 'crash';
  if (writeFailureMarker(event)) return 'write';
  if (isCrashEvent(event)) return 'crash';
  return null;
}

/** Does Sentry carry this event at all? */
function isReportableEvent(event) {
  return reportableKind(event) !== null;
}

/** Low-cardinality client-side signature: what makes two events "the same failure". */
function eventSignature(event) {
  const values = event.exception && event.exception.values;
  const last = Array.isArray(values) && values.length ? values[values.length - 1] : undefined;
  const tags = event.tags || {};
  const url = event.extra && typeof event.extra.url === 'string' ? event.extra.url.split('?')[0] : '';
  return [
    event.level || '',
    last && last.type ? last.type : '',
    String((last && last.value) || event.message || '').slice(0, 200),
    // Tag KEYS (not values — `productId` would make every event unique), plus the few values
    // that name the failing surface.
    Object.keys(tags).sort().join(','),
    tags.collection === undefined ? '' : String(tags.collection),
    tags.status === undefined ? '' : String(tags.status),
    url,
  ].join('|');
}

/**
 * A short, non-reversible id for a signature (FNV-1a 32-bit). The signature itself can hold an
 * error message, which may name a product or a client; PostHog gets only this.
 */
function signatureId(signature) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < signature.length; i += 1) {
    hash ^= signature.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/**
 * Count one reportable occurrence in PostHog. Telemetry must never break error reporting, so a
 * throwing sink is swallowed here.
 */
// The closed set `classifyWiponResponse` emits, and the identifier shape `summarizeWiponRejection`
// admits (global/wipon.js). Re-checked here so a mis-tagged capture cannot put free text in PostHog.
const FISCAL_RESPONSE_CLASS_PATTERN = /^(none|shift-not-open|cashbox-busy|network|unknown|http-(4xx-|5xx-)?\d{3})$/;
const FISCAL_RESPONSE_CODE_PATTERN = /^[A-Za-z0-9_.-]{1,32}$/;

function safeFiscalTag(event, key, pattern) {
  const value = event && event.tags && event.tags[key];
  return typeof value === 'string' && pattern.test(value) ? value : null;
}

function countOccurrence(onReportableEvent, event, kind, signature, outcome) {
  if (typeof onReportableEvent !== 'function') return;
  const values = event && event.exception && event.exception.values;
  const last = Array.isArray(values) && values.length ? values[values.length - 1] : undefined;
  try {
    onReportableEvent({
      kind,
      marker: (event && typeof event === 'object' && writeFailureMarker(event)) || (kind === 'crash' ? 'crash' : null),
      error_type: last && typeof last.type === 'string' ? last.type.slice(0, 80) : null,
      signature: signatureId(signature),
      // 'sent' — went to Sentry; 'repeat' — held back by the repeat window; 'filtered' — dropped
      // by an earlier rule (e.g. the ATL-985 dual-write transport aggregation counts it there).
      outcome,
      // ATL-2035: the Wipon refusal is visible in PostHog by its CLASS and CODE only. The provider
      // message (`extra.wiponRejection.message`) is Sentry-only by decision — a digit-free message
      // can still hold a surname — so nothing from `extra` is ever read here.
      fiscal_response_class: safeFiscalTag(event, 'fiscalResponseClass', FISCAL_RESPONSE_CLASS_PATTERN),
      fiscal_response_code: safeFiscalTag(event, 'fiscalResponseCode', FISCAL_RESPONSE_CODE_PATTERN),
    });
  } catch {
    // ignore
  }
}

/**
 * The last stage of `beforeSend`: keep write failures and crashes, hold back repeats.
 * Returns the event (annotated) or null.
 */
function applyReportingPolicy(event, { windowMs, now, onReportableEvent }) {
  const kind = reportableKind(event);
  if (kind === null) return null;

  const signature = typeof event === 'object' && event ? eventSignature(event) : 'malformed';
  const at = now();
  const open = repeatWindows.get(signature);
  if (open && at - open.openedAt < windowMs) {
    open.suppressed += 1;
    countOccurrence(onReportableEvent, event, kind, signature, 'repeat');
    return null;
  }
  const suppressedSincePrevious = open ? open.suppressed : 0;
  repeatWindows.delete(signature);
  repeatWindows.set(signature, { openedAt: at, suppressed: 0 });
  while (repeatWindows.size > SENTRY_REPEAT_MAX_KEYS) {
    const oldest = repeatWindows.keys().next();
    if (oldest.done) break;
    repeatWindows.delete(oldest.value);
  }
  countOccurrence(onReportableEvent, event, kind, signature, 'sent');
  if (event && typeof event === 'object') {
    event.contexts = {
      ...(event.contexts || {}),
      sentry_repeats: { windowMs, suppressedSincePrevious },
    };
  }
  return event;
}

/**
 * ATL-2035 — a `SupabaseFetchError` that `fetchFailure` already reported. Re-capturing it (e.g.
 * AppStack's daily-stats catch) produced a SECOND event per failure, because the SDK's own
 * "already captured" check is by object identity and the wrapper is a new object. On
 * 2026-10-28..31 that was 708 of 4,755 accepted events.
 */
function isAlreadyReportedFetchFailure(error) {
  return Boolean(
    error && typeof error === 'object' && error.name === 'SupabaseFetchError' && error.reportedToSentry === true,
  );
}

/** TEST-ONLY: clear the repeat windows. */
function resetReportingPolicyForTests() {
  repeatWindows.clear();
}

/**
 * Build the NOISE stage of `beforeSend` (ATL-563 / ATL-985 / ATL-1805): the rules that drop or
 * aggregate an event by what it is. It does not decide whether Sentry carries the event at all —
 * that is the reporting policy `createSentryBeforeSend` applies after it. Exported on its own so
 * these rules stay testable on events the policy would drop anyway.
 *
 * @param {Object} [deps]
 * @param {() => boolean} [deps.isAuthenticated] Returns whether a Firebase user is
 *   currently signed in. Used to distinguish the transient pre-auth Firestore read
 *   race on /login (drop) from a real rules bug for an authenticated user (keep).
 *   If absent or it throws, we treat auth state as unknown and KEEP the event
 *   (never drop a possibly-real permission bug on uncertainty).
 * @returns {(event: object, hint?: object) => object|null}
 */
function createSentryNoiseFilter({ isAuthenticated } = {}) {
  return function beforeSend(event, hint) {
    const error = getOriginalException(hint);

    // Offline connectivity — expected on mobile data, not a bug.
    if (isNetworkNoiseEvent(event, error)) {
      // ATL-985 — with one exception, and it is the whole point of this hook.
      //
      // A dual-write failure is reported AFTER Firestore has committed. Whatever caused it,
      // the outcome is a row that exists in the source of truth and not in Supabase, and no
      // later event will restate it. Dropping it because its proximate cause was a flaky radio
      // discards the loss report, not the noise — and because offline failures are the
      // DOMINANT class here, it discarded most of them. Measured: 192 documents lost across 3
      // orgs in 30 days (5.1%) with the error stream reading clean the entire time.
      //
      // Kept, but aggregated: see DUAL_WRITE_NETWORK_SAMPLE_WINDOW_MS for why a counter is the
      // right instrument for this class and a per-occurrence exception is not.
      if (isDualWriteEvent(event)) return sampleDualWriteTransportEvent(event);
      return null;
    }

    // ATL-1805: a migration pause refuses every write of the organization for the whole run.
    if (isDualWriteMigrationPausedEvent(event)) return sampleDualWriteMigrationPausedEvent(event);

    // Expected user-input validation thrown as an Error.
    if (isFormValidationError(error)) return null;

    // Transient pre-auth permission-denied on /login. Only drop when we positively
    // know no user is signed in; otherwise keep (it could be a real rules bug).
    if (isPermissionDeniedError(error)) {
      let authedKnown = false;
      let authed = true;
      if (typeof isAuthenticated === 'function') {
        try {
          authed = !!isAuthenticated();
          authedKnown = true;
        } catch {
          authedKnown = false;
        }
      }
      if (authedKnown && !authed) return null;
    }

    return event;
  };
}

/**
 * Build the Sentry `beforeSend` hook: one failure → one event, then the noise stage, then the
 * ATL-2035 reporting policy (write failures and crashes only, hourly repeat window).
 *
 * @param {Object} [deps]
 * @param {() => boolean} [deps.isAuthenticated] See `createSentryNoiseFilter`.
 * @param {(properties: object) => void} [deps.onReportableEvent] Called once for EVERY write-failure
 *   or crash occurrence, whether it went to Sentry or not (App.js → PostHog
 *   `sentry_reportable_event`). Properties are kinds and a hashed signature only — no message text.
 * @param {() => number} [deps.now] Clock (tests).
 * @param {number} [deps.repeatWindowMs] Repeat window (tests); defaults to `SENTRY_REPEAT_WINDOW_MS`.
 * @returns {(event: object, hint?: object) => object|null}
 */
function createSentryBeforeSend({ isAuthenticated, onReportableEvent, now = Date.now, repeatWindowMs = SENTRY_REPEAT_WINDOW_MS } = {}) {
  const noiseFilter = createSentryNoiseFilter({ isAuthenticated });
  const policy = { windowMs: repeatWindowMs, now, onReportableEvent };
  return function beforeSend(event, hint) {
    // ATL-2035: one failure, one event. A re-capture is not an occurrence, so it is not counted.
    if (isAlreadyReportedFetchFailure(getOriginalException(hint))) return null;

    const kind = reportableKind(event);
    const out = noiseFilter(event, hint);
    if (out === null) {
      // A write failure the noise stage aggregated (ATL-985 transport, ATL-1805 migration pause)
      // is still an occurrence: count it, so PostHog holds the full rate.
      if (kind !== null) countOccurrence(onReportableEvent, event, kind, eventSignature(event), 'filtered');
      return null;
    }
    return applyReportingPolicy(out, policy);
  };
}

module.exports = {
  SENTRY_IGNORE_ERRORS,
  NETWORK_NOISE_PATTERNS,
  DUAL_WRITE_MARKER_TAG,
  DUAL_WRITE_NETWORK_SAMPLE_WINDOW_MS,
  getOriginalException,
  isNetworkError,
  isNetworkNoiseEvent,
  isDualWriteEvent,
  isDualWriteMigrationPausedEvent,
  isFormValidationError,
  isPermissionDeniedError,
  createSentryNoiseFilter,
  createSentryBeforeSend,
  resetSentryFiltersForTests,
  SENTRY_REPEAT_WINDOW_MS,
  SENTRY_REPORTABLE_POSTHOG_EVENT,
  reportableKind,
  isReportableEvent,
  isAlreadyReportedFetchFailure,
  resetReportingPolicyForTests,
};
